// src/components/ui/OTPInput.tsx
import {
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';

type Props = {
  length?: number;
  onComplete?: (code: string) => void;
  /** Fires on every edit with whatever is typed so far (shorter than `length` while incomplete). */
  onChange?: (code: string) => void;
};

export function OTPInput({ length = 6, onComplete, onChange: onValueChange }: Props) {
  const [values, setValues] = useState<string[]>(() => Array.from({ length }, () => ''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  function setCell(idx: number, char: string) {
    setValues((prev) => {
      const next = [...prev];
      next[idx] = char;
      queueMicrotask(() => onValueChange?.(next.join('')));
      if (next.every((v) => v !== '') && onComplete) {
        // Defer to next tick so the input state settles first.
        queueMicrotask(() => onComplete(next.join('')));
      }
      return next;
    });
  }

  function onChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    // Only the last typed character matters.
    const char = raw.replace(/\D/g, '').slice(-1);
    if (!char) {
      setCell(idx, '');
      return;
    }
    setCell(idx, char);
    if (idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  }

  function onKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (values[idx] === '' && idx > 0) {
        refs.current[idx - 1]?.focus();
        setCell(idx - 1, '');
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      refs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = Array.from({ length }, (_, i) => pasted[i] ?? '');
    setValues(next);
    onValueChange?.(next.join(''));
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
    if (pasted.length === length && onComplete) {
      queueMicrotask(() => onComplete(pasted));
    }
  }

  return (
    <div className="flex items-center justify-center gap-3" onPaste={onPaste}>
      {values.map((v, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          maxLength={1}
          value={v}
          onChange={(e) => onChange(i, e)}
          onKeyDown={(e) => onKeyDown(i, e)}
          autoFocus={i === 0}
          aria-label={`OTP digit ${i + 1}`}
          className="font-mono"
          style={{
            width: 48,
            height: 56,
            textAlign: 'center',
            fontSize: 22,
            color: 'var(--text-primary)',
            background: 'transparent',
            border: 'none',
            borderBottom: `1px solid ${v ? 'var(--text-primary)' : 'var(--hairline)'}`,
            outline: 'none',
            transition: 'border-color 150ms ease',
            caretColor: 'var(--accent)',
          }}
        />
      ))}
    </div>
  );
}
